import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ScanFace, Loader2, Trash2 } from 'lucide-react';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import { API_URL } from '../../utils/api';

const base = (kind, token) => `${API_URL}/api/passkey/${kind}/${encodeURIComponent(token)}`;
const errMsg = (e) => (e?.name === 'NotAllowedError' ? 'A Face ID / ujjlenyomat kérést megszakítottad.' : e?.response?.data?.detail || e?.message || 'Hiba');

export const usePasskeyStatus = (kind, token) => {
  const [st, setSt] = useState(null);
  const reload = () => axios.get(`${base(kind, token)}/status`).then((r) => setSt(r.data)).catch(() => setSt({ registered: false, configured: false }));
  useEffect(() => { if (token) reload(); }, [kind, token]); // eslint-disable-line react-hooks/exhaustive-deps
  return [st, reload];
};

// Belépő képernyőn: egy érintéses Face ID belépés (ha már van mentett passkey)
export function PasskeyLoginButton({ kind, token, onSession }) {
  const [st] = usePasskeyStatus(kind, token);
  const [busy, setBusy] = useState(false);
  if (!st?.registered || !window.PublicKeyCredential) return null;
  const login = async () => {
    setBusy(true);
    try {
      const init = (await axios.post(`${base(kind, token)}/authentication/options`)).data;
      const credential = await startAuthentication({ optionsJSON: init.options });
      const r = await axios.post(`${base(kind, token)}/authentication/verify`, { ceremony_id: init.ceremony_id, credential });
      toast.success(`Üdv, ${r.data.name || ''}!`); onSession(r.data.session);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={login} disabled={busy} className="w-full h-14 rounded-2xl text-white text-base font-bold inline-flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-60" style={{ background: '#0F172A' }} data-testid="passkey-login">
      {busy ? <Loader2 size={18} className="animate-spin" /> : <ScanFace size={20} />} Belépés Face ID-val
    </button>
  );
}

// Bejelentkezés után: „Mentés Face ID-val” / törlés — egységes dolgozói és PM portálon
export function PasskeySetupCard({ kind, token, sessionHeader }) {
  const [st, reload] = usePasskeyStatus(kind, token);
  const [busy, setBusy] = useState(false);
  const [avail, setAvail] = useState(true);
  useEffect(() => { window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable?.().then(setAvail).catch(() => setAvail(false)); }, []);
  if (!st || !st.configured) return null;
  const supported = !!window.PublicKeyCredential;
  const headers = sessionHeader;
  const save = async () => {
    setBusy(true);
    try {
      const init = (await axios.post(`${base(kind, token)}/registration/options`, {}, { headers })).data;
      const credential = await startRegistration({ optionsJSON: init.options });
      await axios.post(`${base(kind, token)}/registration/verify`, { ceremony_id: init.ceremony_id, credential }, { headers });
      toast.success('Face ID mentve — legközelebb egy érintéssel lépsz be'); reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async () => { if (!window.confirm('Törlöd a mentett Face ID belépést ezen a fiókon?')) return; try { await axios.delete(`${base(kind, token)}/credentials`, { headers }); toast.success('Törölve'); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section className="rounded-2xl border bg-white p-3 flex items-center gap-3" style={{ borderColor: '#E6E2DA' }} data-testid="passkey-setup-card">
      <span className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#0F172A', color: '#fff' }}><ScanFace size={20} /></span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800">{st.registered ? `Face ID belépés aktív (${st.count} eszköz)` : 'Belépés Face ID-val'}</p>
        <p className="text-[11px] text-slate-500">{!supported ? 'Ez a böngésző nem támogatja — nyisd meg a telefonod Safari/Chrome böngészőjében.' : st.registered ? 'Legközelebb PIN nélkül, egy érintéssel.' : avail ? 'Mentsd el — legközelebb nem kell PIN.' : 'Ezen az eszközön nincs biometria; PIN marad.'}</p>
      </div>
      {st.registered && <button type="button" onClick={remove} className="w-9 h-9 rounded-lg text-slate-400 hover:text-rose-600 flex items-center justify-center" data-testid="passkey-remove"><Trash2 size={15} /></button>}
      <button type="button" onClick={save} disabled={busy || !avail || !supported} className="px-3 h-10 rounded-xl text-xs font-semibold text-white disabled:opacity-50 inline-flex items-center gap-1.5" style={{ background: '#B85A26' }} data-testid="passkey-save">{busy ? <Loader2 size={14} className="animate-spin" /> : <ScanFace size={14} />} {st.registered ? 'Új eszköz' : 'Mentés'}</button>
    </section>
  );
}
